import { useEffect, useMemo } from "react";
import { Link, Navigate, useParams } from "react-router-dom";
import { motion } from "framer-motion";
import {
  ArrowLeft,
  Clock,
  ChevronRight,
  Check,
  Stethoscope,
  Sparkles,
  ArrowRight,
  BookOpen,
} from "lucide-react";
import { ARTICLES_20, findArticle } from "@/lib/libraryArticles";
import { ARTICLES as LEGACY_ARTICLES } from "@/pages/KnowledgeLibrary";
import { setPageMeta, SITE_URL } from "@/lib/pageMeta";
import ComplianceFooter from "@/components/silentscan/ComplianceFooter";
import { useChatbot } from "@/context/ChatbotContext";

// Legacy deep-dive articles (from the library index) normalized to the page shape.
const normalizeLegacy = (a) =>
  a && {
    slug: a.id,
    question: a.title,
    title: `${a.title} | SilentScan AI`,
    description: a.excerpt,
    category: a.category,
    reading: a.reading,
    intro: a.excerpt,
    sections: (a.body || []).map((p) => ({ h: null, p: [p] })),
    tips: a.key_points || [],
  };

// Public SEO article page — no sign-in required.
export default function ArticlePage() {
  const { slug } = useParams();
  const article = useMemo(
    () => findArticle(slug) || normalizeLegacy(LEGACY_ARTICLES.find((a) => a.id === slug)),
    [slug]
  );
  const { openChatbot } = useChatbot();

  useEffect(() => {
    window.scrollTo(0, 0);
    if (!article) return;
    const path = `/library/${article.slug}`;
    setPageMeta({
      title: article.title,
      description: article.description,
      path,
      jsonLd: [
        {
          "@context": "https://schema.org",
          "@type": "Article",
          headline: article.question,
          description: article.description,
          url: `${SITE_URL}${path}`,
          inLanguage: "en",
          isAccessibleForFree: true,
          author: { "@type": "Organization", name: "SilentScan AI" },
          publisher: { "@type": "Organization", name: "SilentScan AI" },
        },
        {
          "@context": "https://schema.org",
          "@type": "BreadcrumbList",
          itemListElement: [
            { "@type": "ListItem", position: 1, name: "Home", item: `${SITE_URL}/` },
            { "@type": "ListItem", position: 2, name: "Knowledge Library", item: `${SITE_URL}/library` },
            { "@type": "ListItem", position: 3, name: article.question, item: `${SITE_URL}${path}` },
          ],
        },
        {
          "@context": "https://schema.org",
          "@type": "FAQPage",
          mainEntity: [
            {
              "@type": "Question",
              name: article.question,
              acceptedAnswer: { "@type": "Answer", text: article.intro },
            },
          ],
        },
      ],
    });
  }, [article]);

  if (!article) return <Navigate to="/library" replace />;

  const related = ARTICLES_20.filter((a) => a.slug !== article.slug && a.category === article.category);
  const more = ARTICLES_20.filter((a) => a.slug !== article.slug && a.category !== article.category);
  const relatedList = [...related, ...more].slice(0, 4);

  return (
    <div data-testid="article-page" className="min-h-screen bg-[#05050A] text-white">
      <div className="max-w-3xl mx-auto px-6 pt-24 pb-16">
        <Link
          to="/library"
          data-testid="article-back"
          className="inline-flex items-center gap-2 text-xs text-zinc-400 hover:text-white transition-colors"
        >
          <ArrowLeft className="w-3.5 h-3.5" />
          Knowledge Library
        </Link>

        <motion.div
          initial={{ opacity: 0, y: 16 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6, ease: [0.22, 1, 0.36, 1] }}
        >
          <nav aria-label="Breadcrumb" className="mt-5 flex flex-wrap items-center gap-1.5 text-[11px] text-zinc-500">
            <Link to="/" className="hover:text-cyan-300 transition-colors">Home</Link>
            <ChevronRight className="w-3 h-3" />
            <Link to="/library" className="hover:text-cyan-300 transition-colors">Library</Link>
            <ChevronRight className="w-3 h-3" />
            <span className="text-zinc-300 truncate max-w-[220px] sm:max-w-none">{article.question}</span>
          </nav>

          <div className="mt-6 flex items-center gap-3">
            <span className="px-3 py-1 rounded-full bg-cyan-500/10 border border-cyan-400/30 text-[10px] uppercase tracking-widest text-cyan-300 font-semibold">
              {article.category}
            </span>
            <span className="inline-flex items-center gap-1 text-[11px] text-zinc-500">
              <Clock className="w-3 h-3" />
              {article.reading} read
            </span>
          </div>

          <h1
            data-testid="article-title"
            className="mt-4 font-display text-3xl sm:text-4xl lg:text-5xl font-semibold tracking-tight leading-tight"
          >
            {article.question}
          </h1>

          <p className="mt-5 text-base sm:text-lg text-zinc-300 leading-relaxed border-l-2 border-cyan-400/50 pl-5">
            {article.intro}
          </p>

          {/* Body sections */}
          <div className="mt-10 space-y-10">
            {article.sections.map((s, i) => (
              <section key={i} data-testid={`article-section-${i}`}>
                {s.h && (
                  <h2 className="font-display text-xl sm:text-2xl font-semibold text-white tracking-tight">
                    {s.h}
                  </h2>
                )}
                {s.p.map((para, j) => (
                  <p key={j} className="mt-3 text-sm sm:text-base text-zinc-400 leading-relaxed">
                    {para}
                  </p>
                ))}
              </section>
            ))}
          </div>

          {/* Practical takeaways */}
          <div className="mt-10 rounded-3xl border border-emerald-400/25 bg-emerald-500/[0.06] p-6">
            <div className="flex items-center gap-2 text-[10px] uppercase tracking-[0.25em] text-emerald-300 font-semibold">
              <Check className="w-3.5 h-3.5" />
              Everyday takeaways
            </div>
            <ul className="mt-4 space-y-2.5">
              {article.tips.map((t, i) => (
                <li key={i} className="flex items-start gap-3 text-sm text-zinc-200 leading-relaxed">
                  <span className="w-5 h-5 rounded-full bg-emerald-500/15 border border-emerald-400/30 flex items-center justify-center shrink-0 mt-0.5">
                    <Check className="w-3 h-3 text-emerald-400" />
                  </span>
                  {t}
                </li>
              ))}
            </ul>
          </div>

          {/* Professional-care note */}
          <div className="mt-6 rounded-3xl border border-white/10 bg-white/[0.03] p-6 flex items-start gap-4">
            <div className="w-10 h-10 rounded-xl bg-[#0B3D91]/30 border border-cyan-400/20 flex items-center justify-center shrink-0">
              <Stethoscope className="w-5 h-5 text-cyan-300" />
            </div>
            <div>
              <div className="text-sm font-semibold text-white">A note on professional care</div>
              <p className="mt-1 text-sm text-zinc-400 leading-relaxed">
                This article is educational and general. Your situation has its
                own context — for symptoms that are severe, sudden, persistent or
                worrying, consult a qualified medical practitioner.
              </p>
            </div>
          </div>

          {/* CTA */}
          <div className="mt-8 rounded-3xl border border-cyan-400/25 bg-gradient-to-r from-[#0B3D91]/25 to-cyan-500/10 p-6 sm:p-8 flex flex-col sm:flex-row sm:items-center gap-5">
            <div className="w-12 h-12 rounded-2xl bg-gradient-to-br from-[#0B3D91] to-cyan-500 flex items-center justify-center shrink-0 shadow-[0_0_25px_rgba(0,229,255,0.3)]">
              <Sparkles className="w-6 h-6 text-white" />
            </div>
            <div className="flex-1">
              <h2 className="font-display text-xl font-semibold">Check where your lifestyle stands</h2>
              <p className="mt-1 text-sm text-zinc-400 leading-relaxed">
                Take the free 16-question AI check-in for a personal wellness
                score — no sign-up, about 3 minutes.
              </p>
            </div>
            <button
              data-testid="article-checkin-btn"
              onClick={openChatbot}
              className="inline-flex items-center justify-center gap-2 px-6 py-3 rounded-full btn-glow text-white text-sm font-medium shrink-0"
            >
              Start free check-in
              <ArrowRight className="w-4 h-4" />
            </button>
          </div>

          {/* Related articles */}
          <div className="mt-12" data-testid="related-articles">
            <div className="flex items-center gap-2 text-[10px] uppercase tracking-[0.25em] text-zinc-500 font-semibold">
              <BookOpen className="w-3.5 h-3.5" />
              Keep reading
            </div>
            <div className="mt-4 grid sm:grid-cols-2 gap-3">
              {relatedList.map((r) => (
                <Link
                  key={r.slug}
                  to={`/library/${r.slug}`}
                  data-testid={`related-${r.slug}`}
                  className="rounded-2xl border border-white/10 bg-white/[0.03] p-4 hover:border-cyan-400/30 hover:bg-white/[0.05] transition-colors"
                >
                  <div className="text-[10px] uppercase tracking-widest text-cyan-400/80 font-semibold">
                    {r.category}
                  </div>
                  <div className="mt-1 text-sm font-medium text-zinc-100 leading-snug">
                    {r.question}
                  </div>
                </Link>
              ))}
            </div>
          </div>

          <ComplianceFooter variant="issue" />
        </motion.div>
      </div>
    </div>
  );
}
